import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from "components/GlobalContext";
import { Accordion, Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import IDealer, { IDealerCreateOrUpdate, IDealerInitResult } from "models/sql/IDealer";
import DealerService from "services/DealerService";
import { generateLog } from "utilities/MiscUtilities";
import CountryChooser from "components/CountryChooser";
import MarkdownEditor from "components/MarkdownEditor";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import ContactViewer from "components/ContactViewer";
import { useBeforeUnload } from "utilities/UIUtilities";
import DetailFooter from "components/DetailFooter";

interface IDealersDetailProps {
    baseData?: IDealerInitResult;
}

const DealersDetail = (props: IDealersDetailProps) => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const { id }: any = useParams();
    const [dealer, setDealer] = useState<IDealer>({
        dealerId: -1,
        countries: "",
        dealerName: "",
        airEquipmentDiscount: undefined,
        airConsumableDiscount: undefined,
        emailSuffix: "",
        notes: "",
        paymentTerms: "",
        primaryEmail: "",
        primarySalesContact: "",
        state: "",
        states: "",
        systemDataJson: undefined,
        tftSalesman: "",
        zipCode: "",
        defaultRoles: "",
        created: undefined,
        logoUrl: "",
        disableAutoQuoting: undefined,
        canRequestDemo: undefined,
        entityId: undefined,
        demoDiscount: undefined,
        region: "",
        dealerLevel: "",
        type: ""
    });
    const navigate = useNavigate();
    const [hasFormDataChanged, setHasFormDataChanged] = useState(false);
    const types = ["Truck OEM", "Equipment", "Apparatus", "Equipment & Apparatus"];
    const regions = ["US", "International"];
    const levels = ["Channel Partner", "Tier 1", "Tier 2", "Tier 3", "Tier 4"];
    const tftSalesMan = ["Ron Prast", "Kevin Briggs", "Jessica Richard", "AJ Bonnett", "Ben Lehman", "North East", "North Central"];

    useBeforeUnload(
        hasFormDataChanged,
        "You have unsaved changes, please save them before leaving the page.");

    useEffect(
        () => {
            (async () => {
                if (id !== undefined) {
                    globalContext.setLoading(true);

                    const accessToken = await globalContext.getAccessTokenAsync();
                    const result = await DealerService.GetAsync(accessToken, id);

                    if (result.record) {
                        setDealer(result.record);
                    }
                    else {
                        globalContext.showErrorMessage("Unable to load Dealer");
                    }

                    globalContext.setLoading(false);
                }
            })();
        }, [id]);

    const deleteAsync = async () => {
        if (dealer && dealer.dealerId && window.confirm("Are you SURE you want to delete this?")) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();
            await DealerService.DeleteAsync(accessToken, dealer.dealerId);
            globalContext.setLoading(false);
            navigate("/Dealers");
        }
    };

    const submitFormAsync = async () => {
        if (dealer) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();

            const data: IDealerCreateOrUpdate = {
                dealerId: dealer.dealerId,
                dealerName: dealer.dealerName,
                primaryEmail: dealer.primaryEmail,
                primarySalesContact: dealer.primarySalesContact,
                notes: dealer.notes,
                state: dealer.state,
                states: dealer.states,
                tftSalesman: dealer.tftSalesman,
                zipCode: dealer.zipCode,
                systemDataJson: dealer.systemDataJson,
                airEquipmentDiscount: dealer.airEquipmentDiscount,
                airConsumableDiscount: dealer.airConsumableDiscount,
                demoDiscount: dealer.demoDiscount,
                emailSuffix: dealer.emailSuffix,
                paymentTerms: dealer.paymentTerms,
                countries: dealer.countries,
                defaultRoles: dealer.defaultRoles,
                created: dealer.created,
                logoUrl: dealer.logoUrl,
                disableAutoQuoting: dealer.disableAutoQuoting,
                canRequestDemo: dealer.canRequestDemo,
                entityId: dealer.entityId,
                region: dealer.region,
                type: dealer.type,
                dealerLevel: dealer.dealerLevel
            };

            await DealerService.CreateOrUpdateAsync(accessToken, data);
            globalContext.setLoading(false);
            setHasFormDataChanged(false);

            if (data.dealerId === -1) {
                navigate(-1);
            }
        }
    };

    if (dealer) {
        return (
            <>
                <Row>
                    <Col>
                        <Card className="mb-2 mt-2">
                            <Card.Body>
                                <Card.Title>
                                    {
                                        id !== undefined &&
                                        <strong>{dealer.dealerName} ({dealer.dealerId})</strong>
                                    }
                                    {
                                        id === undefined &&
                                        <strong>New Dealer</strong>
                                    }
                                </Card.Title>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Name</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setDealer({ ...dealer, dealerName: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={dealer.dealerName}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Primary Email</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setDealer({ ...dealer, primaryEmail: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={dealer.primaryEmail}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Primary Sales Contact</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setDealer({ ...dealer, primarySalesContact: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={dealer.primarySalesContact}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>TFT Salesman</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={async (e) => {
                                                        setDealer({ ...dealer, tftSalesman: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    value={dealer.tftSalesman}>
                                                    <option key={"empty"} value={""}>-</option>
                                                    {
                                                        tftSalesMan.map((s, index) => <option key={s} value={s}>{s}</option>)
                                                    }
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>States</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setDealer({ ...dealer, states: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={dealer.states}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Country</Form.Label>
                                            <CountryChooser
                                                onChange={newVal => {
                                                    setDealer({ ...dealer, countries: newVal });
                                                    setHasFormDataChanged(true);
                                                }}
                                                defaultValue={dealer.countries}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Postal Code</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setDealer({ ...dealer, zipCode: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={dealer.zipCode}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Payment Terms</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setDealer({ ...dealer, paymentTerms: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={dealer.paymentTerms}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Discount, Air Equipment</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setDealer({ ...dealer, airEquipmentDiscount: parseFloat(e.target.value) });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={dealer.airEquipmentDiscount}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Discount, Air Consumable</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setDealer({ ...dealer, airConsumableDiscount: parseFloat(e.target.value) });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={dealer.airConsumableDiscount}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Discount, Demo Products</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setDealer({ ...dealer, demoDiscount: parseFloat(e.target.value) });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={dealer.demoDiscount}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Entity Id</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setDealer({ ...dealer, entityId: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={dealer.entityId}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Disable Auto Quoting</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={async (e) => {
                                                        setDealer({ ...dealer, disableAutoQuoting: e.target.value == "true" });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    value={dealer.disableAutoQuoting ? "true" : "false"}>
                                                    <option key={"empty"} value={""}>-</option>
                                                    {
                                                        ["true", "false"].map((s, index) => <option key={s} value={s}>{s}</option>)
                                                    }
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Can Request Demos</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={async (e) => {
                                                        setDealer({ ...dealer, canRequestDemo: e.target.value === "true" ? true : e.target.value === "false" ? false : undefined });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    value={dealer.canRequestDemo === true ? "true" : "false"}>
                                                    {
                                                        ["true", "false"].map((s, index) => <option key={s} value={s}>{s}</option>)
                                                    }
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Logo Url</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setDealer({ ...dealer, logoUrl: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={dealer.logoUrl}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Email Suffix<br /><i>MUST include @ symbol and commas between entries</i></Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setDealer({ ...dealer, emailSuffix: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={dealer.emailSuffix}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Type</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={async (e) => {
                                                        setDealer({ ...dealer, type: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    value={dealer.type}>
                                                    <option key={"empty"} value={""}>-</option>
                                                    {
                                                        types.map((s, index) => <option key={s} value={s}>{s}</option>)
                                                    }
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Region</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={async (e) => {
                                                        setDealer({ ...dealer, region: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    value={dealer.region}>
                                                    <option key={"empty"} value={""}>-</option>
                                                    {
                                                        regions.map((s, index) => <option key={s} value={s}>{s}</option>)
                                                    }
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Level</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={async (e) => {
                                                        setDealer({ ...dealer, dealerLevel: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    value={dealer.dealerLevel}>
                                                    <option key={"empty"} value={""}>-</option>
                                                    {
                                                        levels.map((s, index) => <option key={s} value={s}>{s}</option>)
                                                    }
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                {
                                    globalContext.roles.some(r => r === "Web.Contacts") &&
                                    <ContactViewer
                                        source="Dealer"
                                        service={DealerService}
                                        entityId={dealer.dealerId}
                                        canEdit={globalContext.roles.some(r => r === "Web.Admin")}
                                    />
                                }

                                {
                                    globalContext.roles.some(r => r === "Web.SalesRep") &&
                                    <Row className="pb-3">
                                        <Col>
                                            <Accordion style={{ border: "1px solid #0d6efd", borderRadius: "0.375rem" }}>
                                                <Accordion.Item
                                                    eventKey="0"
                                                    style={{
                                                    }}>
                                                    <Accordion.Header><strong>Reps</strong></Accordion.Header>
                                                    <Accordion.Body>
                                                        <Table>
                                                            <thead>
                                                                <tr>
                                                                    <th>Name</th>
                                                                    <th>Email</th>
                                                                    <th>Code</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {
                                                                    dealer.salesReps?.map((e, index) =>
                                                                        <tr
                                                                            key={index}
                                                                            style={{ cursor: "pointer" }}
                                                                            onClick={(event) => {
                                                                                event.preventDefault();
                                                                                event.stopPropagation();
                                                                                window.open(`/Reps/Detail/${e.salesRepId}`)
                                                                            }}>
                                                                            <td>
                                                                                <FontAwesomeIcon icon={faUpRightFromSquare as IconProp} /> {e.fullName}
                                                                            </td>
                                                                            <td>
                                                                                {e.email}
                                                                            </td>
                                                                            <td>
                                                                                {e.code}
                                                                            </td>
                                                                        </tr>
                                                                    )
                                                                }
                                                            </tbody>
                                                        </Table>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            </Accordion>
                                        </Col>
                                    </Row>
                                }

                                {
                                    globalContext.roles.some(r => r === "Web.Admin") &&
                                    <Row className="align-items-end">
                                        <Col>
                                            <Form.Group className="mb-3" >
                                                <Form.Label>Default Roles</Form.Label>
                                                {
                                                    <Form.Control
                                                        type="text"
                                                        onChange={
                                                            (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                setDealer({ ...dealer, defaultRoles: e.target.value });
                                                                setHasFormDataChanged(true);
                                                            }
                                                        }
                                                        value={dealer.defaultRoles}
                                                    />
                                                }
                                            </Form.Group></Col>
                                    </Row>
                                }

                                {
                                    globalContext.roles.some(r => r === "Web.Admin") &&
                                    <Row className="align-items-end">
                                        <Col>
                                            <Card className="mb-2">
                                                <Card.Body>
                                                    <Card.Title>
                                                        <strong>Log</strong><br />
                                                    </Card.Title>
                                                    {generateLog<IDealer>(dealer)}
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                }
                                {
                                    globalContext.roles.some(r => r === "Web.Admin") &&
                                    <Row>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Admin Notes</Form.Label>
                                            <MarkdownEditor
                                                noun="Note"
                                                onChange={newVal => {
                                                    setDealer({ ...dealer, notes: newVal });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={dealer.notes || ""}
                                            />
                                        </Form.Group>
                                    </Row>
                                }
                            </Card.Body>
                        </Card>
                    </Col>
                </Row >

                {
                    globalContext.roles.some(r => r === "Web.Admin") &&
                    <DetailFooter
                        hasFormDataChanged={hasFormDataChanged}
                        deleteAsync={deleteAsync}
                        submitFormAsync={submitFormAsync}
                    />
                }
            </>
        );
    }
    else {
        return (
            <>
                <h1>There was an issue retreiving the dealer, please try again.</h1>
            </>
        );
    }
}

export default DealersDetail;
